// Externals
import type { EventMapV3 } from '@makemydeal/dr-common-data-layer-utils';
import { buildUserEventV3 } from '@makemydeal/dr-common-data-layer-utils';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { CdlEventInfo } from '@makemydeal/dr-sales-checkin-types';

// Types

// Trade-In action types consumed for CDL analytics.
// Duplicated here (rather than imported from @makemydeal/dr-activities-tradein)
// to avoid a project-level circular dependency between activities-tradein and
// sales-checkin-store. Keep these values in sync with the Trade-In activity's
// action types.
const TradeInActions = {
    OPEN_TRADE_IN: 'OPEN_TRADE_IN',
    TRADE_IN_DECISION_PAGE_DISPLAYED: 'TRADE_IN_DECISION_PAGE_DISPLAYED',
    TRADE_IN_DECISION_TYPE_CHANGED: 'TRADE_IN_DECISION_TYPE_CHANGED',
    TRADE_IN_DECISION_CLICKED: 'TRADE_IN_DECISION_CLICKED',
    TRADE_IN_COMPLETED_CONTINUE: 'TRADE_IN_COMPLETED_CONTINUE',
    REMOVE_TRADE_IN: 'REMOVE_TRADE_IN',
    CDL_CANCEL_TRADE_IN_BUTTON_CLICKED: 'CDL_CANCEL_TRADE_IN_BUTTON_CLICKED',
    CDL_TRADE_IN_BACK_BUTTON_CLICKED: 'CDL_TRADE_IN_BACK_BUTTON_CLICKED',
    TRADE_IN_ESTIMATE_DISPLAYED: 'TRADE_IN_ESTIMATE_DISPLAYED',
    TRADE_IN_ESTIMATION_SUCCESS: 'TRADE_IN_ESTIMATION_SUCCESS',
    TRADE_IN_CONDITION_SELECTED: 'TRADE_IN_VEHICLE_CONDITION_SELECTED',
    APPLY_TRADE_IN_EQUIPMENT_OPTIONS: 'APPLY_TRADE_IN_EQUIPMENT_OPTIONS',
    TRADE_IN_CONDITION_APPLIED: 'TRADE_IN_CONDITION_APPLIED'
} as const;

// Trade-In route paths consumed for CDL analytics. Kept in sync with the
// Trade-In activity's route constants for the same reason as above.
const tradeInRoutes = {
    tradeIn: '/tradeIn',
    vehicleInfo: '/vehicleInfo',
    vehicleCondition: '/vehicleCondition',
    equipmentOptions: '/equipmentOptions',
    equipmentOptionsOne: '/equipmentOptionsOne',
    tradeInDecision: '/tradeInDecision'
};

const tradeInSources = {
    kbbEstimate: 'SD',
    manualEntry: 'USER',
    ico: 'ICO',
    subaruGtp: 'SUBARUGTP'
} as const;

function getTradeInRoutes(state: any) {
    return {
        previousRoute: state.tradeInComponent?.previousUserLocationRoute?.split('?')[0] || '',
        tradeRoutes: tradeInRoutes
    };
}

const buildEventWrapper = (eventData: CdlEventInfo) => (_action: AnyFSA) => {
    return buildUserEventV3(eventData);
};

export const tradeInCdlEvents: EventMapV3 = {
    [TradeInActions.OPEN_TRADE_IN]: buildEventWrapper({
        action: 'Click',
        element: 'Open Trade-In Section Link',
        location: ['Header', 'Navigation'],
        intention: 'Update Trade-In information',
        tagIndex: 'dc-sci-021'
    }),

    [TradeInActions.TRADE_IN_DECISION_PAGE_DISPLAYED]: (action: any, currentState: any) => {
        const { previousRoute, tradeRoutes } = getTradeInRoutes(currentState);

        if (previousRoute === tradeRoutes.tradeIn || previousRoute === tradeRoutes.vehicleInfo) {
            return buildEventWrapper({
                action: 'Click',
                element: 'Continue to Estimate Button',
                location: ['Trade-In'],
                intention: 'Continue to estimate method',
                tagIndex: 'dc-sci-033'
            })(action);
        }
        return { event: {} };
    },

    [TradeInActions.TRADE_IN_DECISION_TYPE_CHANGED]: buildEventWrapper({
        action: 'Click',
        element: 'Select Trade-In Appraisal Method Button',
        location: ['Trade-In'],
        intention: 'Select desired form of trade-in appraisal',
        tagIndex: 'dc-sci-028'
    }),

    [TradeInActions.TRADE_IN_DECISION_CLICKED]: (action: any, currentState: any): any => {
        const source = currentState.tradeInComponent?.source;

        if (source === tradeInSources.kbbEstimate) {
            return buildEventWrapper({
                action: 'Click',
                element: 'Continue to Condition Button',
                location: ['Trade-In'],
                intention: 'Continue to trade-in vehicle condition',
                tagIndex: 'dc-sci-029'
            })(action);
        }
        if (source === tradeInSources.ico || source === tradeInSources.manualEntry) {
            return buildEventWrapper({
                action: 'Click',
                element: 'Continue to Estimate Button',
                location: ['Trade-In'],
                intention: 'Continue to estimate method',
                tagIndex: 'dc-sci-022'
            })(action);
        }
        return undefined;
    },

    [TradeInActions.TRADE_IN_COMPLETED_CONTINUE]: buildEventWrapper({
        action: 'Click',
        element: 'Browse Inventory Button',
        location: ['Trade-In'],
        intention: 'Browse inventory after trade-in evaluation',
        tagIndex: 'dc-sci-034'
    }),

    [TradeInActions.REMOVE_TRADE_IN]: buildEventWrapper({
        action: 'Click',
        element: 'Remove Trade-In Link',
        location: ['Trade-In'],
        intention: 'Remove Trade-In',
        tagIndex: 'dc-sci-025'
    }),

    [TradeInActions.CDL_CANCEL_TRADE_IN_BUTTON_CLICKED]: buildEventWrapper({
        action: 'Click',
        element: 'Cancel Trade-In Button',
        location: ['Trade-In'],
        intention: 'Cancel trade-in process',
        tagIndex: 'dc-sci-070'
    }),

    // Every '< Back' control in the Trade-In flow dispatches this, so one entry covers them all.
    [TradeInActions.CDL_TRADE_IN_BACK_BUTTON_CLICKED]: buildEventWrapper({
        action: 'Click',
        element: 'Trade-In Back Button',
        location: ['Trade-In'],
        intention: 'Return to previous Trade-In step',
        tagIndex: 'dc-sci-043'
    }),

    [TradeInActions.TRADE_IN_ESTIMATE_DISPLAYED]: buildEventWrapper({
        action: 'View',
        element: 'Trade-In Estimated Value Display',
        location: ['Trade-In'],
        intention: 'View estimated trade-in value',
        tagIndex: 'dc-sci-066'
    }),

    [TradeInActions.TRADE_IN_ESTIMATION_SUCCESS]: (action: any, currentState: any) => {
        const { previousRoute, tradeRoutes } = getTradeInRoutes(currentState);

        if (previousRoute === tradeRoutes.equipmentOptions) {
            return buildEventWrapper({
                action: 'Click',
                element: 'Continue to Trade-In Summary Button',
                location: ['Trade-In'],
                intention: 'Continue to trade-in summary',
                tagIndex: 'dc-sci-027'
            })(action);
        }
        return { event: {} };
    },

    // TRADE IN KBB ESTIMATE FLOW
    [TradeInActions.TRADE_IN_CONDITION_SELECTED]: buildEventWrapper({
        action: 'Click',
        element: 'Evaluate Vehicle Condition Button',
        location: ['Trade-In'],
        intention: 'Evaluate vehicle condition',
        tagIndex: 'dc-sci-026'
    }),

    [TradeInActions.APPLY_TRADE_IN_EQUIPMENT_OPTIONS]: (action: any, currentState: any) => {
        const { previousRoute, tradeRoutes } = getTradeInRoutes(currentState);

        if (previousRoute === tradeRoutes.vehicleCondition || previousRoute === tradeRoutes.equipmentOptions) {
            return buildEventWrapper({
                action: 'Click',
                element: 'Continue to Options Button',
                location: ['Trade-In'],
                intention: 'Continue to trade-in vehicle options',
                tagIndex: 'dc-sci-030'
            })(action);
        }
        return { event: {} };
    },

    [TradeInActions.TRADE_IN_CONDITION_APPLIED]: (action: any, currentState: any) => {
        const { previousRoute, tradeRoutes } = getTradeInRoutes(currentState);

        if (previousRoute === tradeRoutes.tradeInDecision || previousRoute === tradeRoutes.equipmentOptionsOne) {
            return buildEventWrapper({
                action: 'Click',
                element: 'Continue to Details Button',
                location: ['Trade-In'],
                intention: 'Continue to trade-in vehicle details',
                tagIndex: 'dc-sci-031'
            })(action);
        }
        return { event: {} };
    }
};
